import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TURKISH_DICTIONARY, isValidTurkishWord } from '../data/turkishWords';
import '../styles/LoveWordle.css';

const KEYBOARD_ROWS = [
  ['E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Ğ', 'Ü'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ş', 'İ'],
  ['ENTER', 'Z', 'C', 'V', 'B', 'N', 'M', 'Ö', 'Ç', '⌫']
];

const MAX_GUESSES = 6;

// Date-based daily word determinator
const getDailyWord = (length) => {
  const words = TURKISH_DICTIONARY[length] || TURKISH_DICTIONARY[5];
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    hash = (hash * 31 + dateStr.charCodeAt(i)) >>> 0;
  }
  return words[hash % words.length];
};

const getRandomWord = (length, excludeWord) => {
  const words = TURKISH_DICTIONARY[length] || TURKISH_DICTIONARY[5];
  const pool = words.filter((w) => w !== excludeWord);
  return pool[Math.floor(Math.random() * pool.length)] || words[0];
};

const LoveWordle = () => {
  const [wordLength, setWordLength] = useState(5);
  const [targetWord, setTargetWord] = useState(() => getDailyWord(5));
  const [isDaily, setIsDaily] = useState(true);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameStatus, setGameStatus] = useState('IN_PROGRESS'); // IN_PROGRESS, WON, LOST
  const [shakeRowIndex, setShakeRowIndex] = useState(null);
  const [revealRowIndex, setRevealRowIndex] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  // Start new game with chosen length
  const startNewGame = useCallback((len = wordLength, mode = 'random') => {
    let word;
    if (mode === 'daily') {
      word = getDailyWord(len);
      setIsDaily(true);
    } else {
      word = getRandomWord(len, targetWord);
      setIsDaily(false);
    }
    setWordLength(len);
    setTargetWord(word);
    setGuesses([]);
    setCurrentGuess('');
    setGameStatus('IN_PROGRESS');
    setShakeRowIndex(null);
    setRevealRowIndex(null);
    setStatusMessage('');
  }, [wordLength, targetWord]);

  // Handle word length tab switch
  const handleLengthChange = (len) => {
    if (len === wordLength) return;
    startNewGame(len, 'daily');
  };

  // Convert Turkish character to uppercase accurately
  const normalizeChar = (ch) => {
    if (ch === 'i') return 'İ';
    if (ch === 'ı') return 'I';
    return ch.toLocaleUpperCase('tr-TR');
  };

  // Check guess validity and update game state
  const submitGuess = useCallback(() => {
    if (gameStatus !== 'IN_PROGRESS') return;

    if (currentGuess.length < wordLength) {
      setShakeRowIndex(guesses.length);
      setStatusMessage(`${wordLength} harfli bir kelime girmelisin!`);
      setTimeout(() => {
        setShakeRowIndex(null);
        setStatusMessage('');
      }, 1500);
      return;
    }

    // Dictionary Validation Check
    if (!isValidTurkishWord(currentGuess, wordLength)) {
      setShakeRowIndex(guesses.length);
      setStatusMessage('Geçersiz kelime');
      setTimeout(() => {
        setShakeRowIndex(null);
        setStatusMessage('');
      }, 1400);
      return;
    }

    const nextGuesses = [...guesses, currentGuess];
    setRevealRowIndex(guesses.length);
    setGuesses(nextGuesses);
    setCurrentGuess('');

    setTimeout(() => {
      setRevealRowIndex(null);
    }, 400);

    if (currentGuess === targetWord) {
      setGameStatus('WON');
      setStatusMessage('Tebrikler! Kelimeyi bildin 💕');
    } else if (nextGuesses.length >= MAX_GUESSES) {
      setGameStatus('LOST');
      setStatusMessage(`Doğru Kelime: ${targetWord}`);
    }
  }, [currentGuess, gameStatus, guesses, targetWord, wordLength]);

  const handleKeyPress = useCallback((char) => {
    if (gameStatus !== 'IN_PROGRESS') return;

    if (char === 'ENTER') {
      submitGuess();
      return;
    }

    if (char === '⌫' || char === 'BACKSPACE') {
      setCurrentGuess((prev) => prev.slice(0, -1));
      return;
    }

    if (currentGuess.length < wordLength) {
      setCurrentGuess((prev) => prev + char);
    }
  }, [gameStatus, submitGuess, currentGuess.length, wordLength]);

  // Physical keyboard support
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        handleKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleKeyPress('BACKSPACE');
      } else if (e.key.length === 1) {
        const char = normalizeChar(e.key);
        // Valid Turkish uppercase letter regex
        if (/^[A-ZÇĞİÖŞÜI]$/.test(char)) {
          e.preventDefault();
          handleKeyPress(char);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleKeyPress]);

  // Evaluate letter matches with duplicate handling
  const evaluateGuess = useCallback((guess, target) => {
    const result = Array(guess.length).fill('absent');
    const targetArr = target.split('');
    const guessArr = guess.split('');

    // First pass: mark correct positions
    for (let i = 0; i < guessArr.length; i++) {
      if (guessArr[i] === targetArr[i]) {
        result[i] = 'correct';
        targetArr[i] = null;
        guessArr[i] = null;
      }
    }

    // Second pass: mark present letters
    for (let i = 0; i < guessArr.length; i++) {
      if (guessArr[i] !== null) {
        const targetIndex = targetArr.indexOf(guessArr[i]);
        if (targetIndex !== -1) {
          result[i] = 'present';
          targetArr[targetIndex] = null;
        }
      }
    }

    return result;
  }, []);

  // Keyboard letter color map
  const keyboardStatuses = useMemo(() => {
    const statuses = {};
    guesses.forEach((guess) => {
      const evaluation = evaluateGuess(guess, targetWord);
      guess.split('').forEach((letter, i) => {
        const current = statuses[letter];
        const evaluated = evaluation[i];

        if (evaluated === 'correct') {
          statuses[letter] = 'correct';
        } else if (evaluated === 'present' && current !== 'correct') {
          statuses[letter] = 'present';
        } else if (evaluated === 'absent' && !current) {
          statuses[letter] = 'absent';
        }
      });
    });
    return statuses;
  }, [guesses, targetWord, evaluateGuess]);

  return (
    <div className="love-wordle-container">
      {/* Word Length Selector Tabs (3 to 10 letters) */}
      <div className="wordle-length-selector" role="tablist" aria-label="Kelime Uzunluğu Seçimi">
        {[3, 4, 5, 6, 7, 8, 9, 10].map((len) => (
          <button
            key={len}
            role="tab"
            aria-selected={wordLength === len}
            className={`length-tab ${wordLength === len ? 'active' : ''}`}
            onClick={() => handleLengthChange(len)}
          >
            {len}
          </button>
        ))}
      </div>

      {/* Subheader: Mode & Action Buttons */}
      <div className="wordle-sub-bar">
        <span className="wordle-mode-badge">
          {isDaily ? 'Günün Kelimesi' : 'Rastgele Oyun'} • {wordLength} Harf
        </span>
        <div className="wordle-btn-group">
          <button
            className="wordle-btn"
            onClick={() => startNewGame(wordLength, 'random')}
            type="button"
            title="Yeni bir rastgele kelime başlat"
          >
            Yeni Oyun
          </button>
          {!isDaily && (
            <button
              className="wordle-btn"
              onClick={() => startNewGame(wordLength, 'daily')}
              type="button"
              title="Günün kelimesine dön"
            >
              Günün Kelimesi
            </button>
          )}
        </div>
      </div>

      {/* Feedback banner */}
      {statusMessage && (
        <div className="wordle-toast-banner" role="alert">
          {statusMessage}
        </div>
      )}

      {/* Wordle Grid */}
      <div
        className="wordle-grid-wrapper"
        style={{
          '--grid-cols': wordLength,
          '--cell-max-width': wordLength >= 8 ? '36px' : wordLength >= 6 ? '44px' : '52px',
        }}
      >
        {Array.from({ length: MAX_GUESSES }).map((_, rowIndex) => {
          const isCurrentRow = rowIndex === guesses.length;
          const guess = guesses[rowIndex] || (isCurrentRow ? currentGuess : '');
          const isShaking = shakeRowIndex === rowIndex;
          const isRevealing = revealRowIndex === rowIndex;
          const evaluation = guesses[rowIndex] ? evaluateGuess(guesses[rowIndex], targetWord) : null;

          return (
            <div
              key={rowIndex}
              className={`wordle-row ${isShaking ? 'row-shake' : ''} ${isRevealing ? 'row-reveal' : ''}`}
            >
              {Array.from({ length: wordLength }).map((__, colIndex) => {
                const letter = guess[colIndex] || '';
                const status = evaluation ? evaluation[colIndex] : '';

                return (
                  <div
                    key={colIndex}
                    className={`wordle-cell ${letter ? 'cell-filled' : ''} ${status ? `status-${status}` : ''}`}
                    style={{
                      animationDelay: evaluation ? `${colIndex * 100}ms` : '0ms',
                    }}
                  >
                    {letter}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Virtual Turkish Touch Keyboard */}
      <div className="wordle-keyboard" aria-label="Sanal Klavye">
        {KEYBOARD_ROWS.map((row, rIdx) => (
          <div key={rIdx} className="keyboard-row">
            {row.map((key) => {
              const status = keyboardStatuses[key] || '';
              const isWide = key === 'ENTER' || key === '⌫';
              return (
                <button
                  key={key}
                  type="button"
                  className={`key-btn ${isWide ? 'key-wide' : ''} ${status ? `key-${status}` : ''}`}
                  onClick={() => handleKeyPress(key)}
                  aria-label={key === '⌫' ? 'Sil' : key}
                >
                  {key}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LoveWordle;
